// @ts-strict-ignore
import React, { useContext } from 'react';
import { StyledDropdownOptionTooltip, StyledRuleNumber } from './styles';
import {
  findRule,
  getLocalizedIndex,
  getPathToParent,
  isGroupRule,
  isThresholdRule,
} from 'flow/service/rules';
import type { RuleBuilderRule, RuleInsertionPosition, RuleTree } from 'flow/service/rules';
import { DropdownMenu, Icon, IconName } from '@usealloy/component-library';
import { RuleBuilderContext } from '../../context';
import {
  createWorkflowHistoryThunk,
  deleteRuleFromStateThunk,
  InsertThresholdFromThresholdPayload,
  InsertThresholdFromWorkflowStatePayload,
  insertThresholdIntoStateThunk,
} from 'flow/store/redux/thunks';
import { UncontrolledTooltip } from 'reactstrap';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectRuleBuilderIsDragging,
  selectRuleBuilderActiveRuleNumberDropdown,
  selectThresholdById,
  selectRuleBuilderHoveredRuleIndex,
  selectRuleBuilderHasPendingGroup,
} from 'flow/store/redux/selectors';
import { generateThreshold } from 'flow/store/engine/update/thresholds';
import { getCanDeleteRule, isInstanceIndexMatch } from './helpers';
import type { ConnectDragSource } from 'react-dnd';
import {
  setRuleBuilderActiveRuleNumberDropdownId,
  setRuleBuilderHighlightedRuleId,
  setRuleBuilderPendingGroupInfo,
  setSelectedThresholdSimThresholdId,
} from 'flow/store/redux/slice';
import { UserContext } from '~/dashboard/context';
import { selectThresholdSimDisabledReason } from 'flow/store/redux/selectors/workflowFeatures';
import { selectThresholdSimParamsByPublicId } from '~/flow/thresholdSimulator/redux/selectors';
import { getThresholdSimDisabledMessage } from '../../ThresholdSimulatorModal/helpers';
import { isEditable } from '../../helpers';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import { generatePublicId } from 'flow/service/publicIds';
import type { Action } from 'flow/store/engine/types';

type RuleNumberProps = {
  index: number;
  rule: RuleBuilderRule;
  insertPosition: {
    above: RuleInsertionPosition;
    below: RuleInsertionPosition;
  };
  dragSource?: ConnectDragSource;
};

/* Ui component that renders rule number for RuleTemplate */
export default function RuleNumber({
  index, rule, dragSource, insertPosition,
}: RuleNumberProps) {
  const dispatch = useAppDispatch();

  const activeDropdown = useAppSelector(selectRuleBuilderActiveRuleNumberDropdown);
  const hoveredRuleIndex = useAppSelector(selectRuleBuilderHoveredRuleIndex);
  const isDragging = useAppSelector(selectRuleBuilderIsDragging);
  const hasPendingGroup = useAppSelector(selectRuleBuilderHasPendingGroup);

  // only populated if rule is of type threshold.
  const threshold = useAppSelector(selectThresholdById(rule.id));

  const thresholdSimParams = useAppSelector(selectThresholdSimParamsByPublicId(rule.id));
  const thresholdSimDisabledReason = useAppSelector(selectThresholdSimDisabledReason(rule.id));

  const { featureFlags } = useContext(UserContext);
  const {
    sourceAction,
    instanceId,
    editMode,
    ruleTree,
    ruleTreeHasMultipleThresholds: hasMultipleThresholds,
  } = useContext(RuleBuilderContext);

  const isDropdownOpen = isInstanceIndexMatch(activeDropdown, instanceId, index);
  const isLineHovered = isInstanceIndexMatch(hoveredRuleIndex, instanceId, index);
  const canOpenGroup = isThresholdRule(rule) && !hasPendingGroup;

  function onAddThreshold(position: RuleInsertionPosition) {
    const newThreshold = threshold
      ? generateThreshold({ fromThreshold: threshold })
      : undefined;
    const newThresholdId = newThreshold?.publicId || generatePublicId('threshold');

    const stateChange = () => {
      // arguments to either insert a new threshold that is generated from an existing threshold,
      // or insert a new threshold generated from workflow state
      const insertionArgs = newThreshold ? {
        action: sourceAction as Action,
        treeRoot: ruleTree as RuleTree,
        insertProps: {
          threshold: newThreshold,
          relativelyPositionedRule: rule,
          position,
        },
      } satisfies InsertThresholdFromThresholdPayload : {
        action: sourceAction as Action,
        treeRoot: ruleTree as RuleTree,
        insertProps: {
          publicId: newThresholdId,
          relativelyPositionedRule: rule,
          position,
        },
      } satisfies InsertThresholdFromWorkflowStatePayload;

      dispatch(insertThresholdIntoStateThunk(insertionArgs));
    };

    dispatch(
      createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Add,
          uiEffectContext: createUiEffectContext(changeDescription.threshold,
            {
              publicId: newThresholdId,
              actionId: sourceAction.publicId,
            }),
        },
      ),
    );
  }

  const onOpenGroup = () => {
    const parentRulePath = getPathToParent(rule.data.path);
    const parentRule = findRule(ruleTree, { rulePath: parentRulePath });

    dispatch(
      setRuleBuilderPendingGroupInfo({
        parentRuleId: parentRule.id,
        startIndex: getLocalizedIndex(rule.data.path),
      }),
    );
  };

  function onDeleteRule() {
    const uiEffectContext = isGroupRule(rule)
      ? createUiEffectContext(changeDescription.group, { actionId: sourceAction.publicId })
      : createUiEffectContext(changeDescription.threshold, {
        publicId: rule.id,
        actionId: sourceAction.publicId,
      });


    const stateChange = () => dispatch(
      deleteRuleFromStateThunk({
        action: sourceAction,
        treeRoot: ruleTree,
        rule,
      }),
    );

    dispatch(
      createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Remove,
          uiEffectContext,
        },
      ),
    );
  }

  const dropdownItemArgs = [
    {
      text: 'Add above',
      iconName: IconName.ArrowUp,
      onSelect: () => onAddThreshold(insertPosition.above),
      dataDogActionName: 'line-number-menu-add-above',
    },

    {
      text: 'Add below',
      iconName: IconName.ArrowDown,
      onSelect: () => onAddThreshold(insertPosition.below),
      dataDogActionName: 'line-number-menu-add-below',
    },

    ...(canOpenGroup ? [{
      text: 'Open Group (',
      iconName: IconName.AddLayer,
      onSelect: onOpenGroup,
      dataDogActionName: 'line-number-menu-create-group',
    }] : []),

    ...(isGroupRule(rule) ? [{
      text: 'Ungroup',
      iconName: IconName.Ungroup,
      onSelect: onDeleteRule,
      dataDogActionName: 'line-number-menu-remove-group',
    }] : []),

    ...(featureFlags?.thresholdSimulator && isThresholdRule(rule) ? [{
      text: 'Threshold Simulator',
      iconName: IconName.Evaluations,
      onSelect: () => dispatch(setSelectedThresholdSimThresholdId(rule.id)),
      dataDogActionName: 'line-number-menu-threshold-simulator',
      disabled: Boolean(thresholdSimDisabledReason),
      tooltip: {
        content: (thresholdSimDisabledReason && thresholdSimParams)
          ? getThresholdSimDisabledMessage(
            thresholdSimDisabledReason,
            thresholdSimParams,
          )
          : '',
      },
    }] : []),

    ...(getCanDeleteRule(sourceAction, rule, hasMultipleThresholds) ? [{
      text: 'Delete',
      iconName: IconName.Trash,
      onSelect: onDeleteRule,
      dataDogActionName: 'line-number-menu-delete',
      color: 'var(--cl-red-500)',
    }] : []),
  ];

  const onMouseHover = (payload: RuleBuilderRule['id'] | null) => (
    isThresholdRule(rule)
      ? () => dispatch(setRuleBuilderHighlightedRuleId(payload))
      : undefined
  );

  const buildDropdownOption = option => (
    <DropdownMenu.Item
      onSelect={option.onSelect}
      data-dd-action-name={option.dataDogActionName}
      disabled={option.disabled || false}
      style={{ color: option.color }}
      key={option.text}
    >
      <Icon icon={option.iconName} color={option.color} />
      {option.text}
    </DropdownMenu.Item>
  );

  const buildToolTipDropdownOption = option => (
    <StyledDropdownOptionTooltip
      content={option.tooltip.content}
      hide={!option.disabled || false}
      side="top"
    >
      {buildDropdownOption(option)}
    </StyledDropdownOptionTooltip>
  );

  return (
    <>
      <StyledRuleNumber
        id={`rule-number-${rule.id}`}
        data-dd-action-name={`rule-line-number-${index}`}
        ref={dragSource}
        isDraggable={Boolean(dragSource)}
        isLineHovered={isLineHovered}
        onClick={() => {
          if (!isEditable(editMode)) return;
          dispatch(setRuleBuilderActiveRuleNumberDropdownId({
            instanceId,
            index,
          }));
        }}
        onMouseEnter={onMouseHover(rule.id)}
        onMouseLeave={onMouseHover(null)}
      >
        {index}
        {!isDropdownOpen
          && !isDragging
          && isEditable(editMode)
          && (
            <UncontrolledTooltip
              modifiers={tooltipZIndexModifiers}
              target={`rule-number-${rule.id}`}
              trigger="hover"
            >
              {dragSource ? <>Hold to drag<br />Click to view menu</> : 'Click to view menu'}
            </UncontrolledTooltip>
          )}
      </StyledRuleNumber>
      <DropdownMenu
        trigger={<div />}
        isOpen={isDropdownOpen}
        disabled={!isEditable(editMode)}
        onOpenChange={isOpen => {
          if (!isOpen) {
            dispatch(setRuleBuilderActiveRuleNumberDropdownId(null));
          }
        }}
      >
        {dropdownItemArgs.map(option => (option.tooltip
          ? buildToolTipDropdownOption(option)
          : buildDropdownOption(option)
        ))}
      </DropdownMenu>
    </>
  );
}
